import { useEffect, useMemo, useState } from "react";
import {
	Card,
	Table,
	Button,
	Row,
	Col,
	Form,
	Tab,
	Tabs,
} from "react-bootstrap";
import Modal from "../../common/components/ThemedModal";
import { useLocation, useNavigate } from "react-router-dom";
import { createTeam, updateTeam } from "../../../services/teamService";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { calculateRiderValue } from "../../../utils/utils";
import Prizes from "../../Fantasy/Components/Prizes";
import uciPoints from "../../../assets/uci_points.json";
import uciTeamPoints from "../../../assets/uci_team_points.json";
import { LuDot } from "react-icons/lu";
import { IoWarning } from "react-icons/io5";
import { HiUserGroup } from "react-icons/hi2";
import { FaSave } from "react-icons/fa";
import { MdFormatListNumbered } from "react-icons/md";
import {
	BrandTeam,
	CreateTeamInput,
	EditTeamRider,
	initialBrandTeam,
	Rating,
	Rider,
	TeamHistory,
	TeamWithRider,
} from "../../../types/model";
import { Gender } from "../../../constants/constants";
import { RootState } from "../../../store/store";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { getBrandTeams } from "../../../services/brandTeamService";
import SelectBrandTeamCard from "./SelectBrandTeamCard";
import BudgetModeRiderCard from "./BudgetModeRiderCard";
import BudgetRemainingCard from "./BudgetRemainingCard";
import BudgetModeRulesCard from "./BudgetModeRulesCard";
import BudgetModeEligibleRiderCard from "./BudgetModeEligibleRiderCard";
import { setShowLoginModal } from "../../../store/slices/userSlice";
import UCIPointsModal from "./UCIPointsModal";
import SeasonRulesCard from "./SeasonRulesCard";

interface BudgetModeProps {
	numberOfMen: number;
	numberOfWomen: number;
	budget: number;
	eligibleRiders: Rider[];
	team: TeamWithRider;
	setTeam: (team: any) => void;
	globalLeague: any;
	disable?: boolean;
}

const BudgetMode = ({
	numberOfMen,
	numberOfWomen,
	budget,
	eligibleRiders,
	team,
	disable,
}: BudgetModeProps) => {
	const { selectedSeason } = useAppSelector(
		(state: RootState) => state.fantasy,
	);

	const dispatch = useAppDispatch();

	const { isLoggedIn } = useAppSelector((state: RootState) => state.user);
	const [riders, setRiders] = useState<EditTeamRider[]>([]);
	const [uciTeams, setUciTeams] = useState<any[]>([]);
	const [selectedBrandTeam, setSelectedBrandTeam] =
		useState<BrandTeam>(initialBrandTeam);

	const [brandTeams, setBrandTeams] = useState<BrandTeam[]>([]);
	const [brandTeamLoading, setBrandTeamLoading] = useState(false);
	const [teamRiders, setTeamRiders] = useState<any[]>([]);
	const [sortKey, setSortKey] = useState("last_name");
	const [sortOrder, setSortOrder] = useState("asc");
	const [filterGender, setFilterGender] = useState("");
	const [filterCountry, setFilterCountry] = useState("");
	const [viewScoring, setViewScoring] = useState(false);
	const [budgetRemaining, setBudgetRemaining] = useState<number>(budget);
	const [showDisabledModal, setShowDisabledModal] = useState(false);
	const [showIneligibleModal, setShowIneligibleModal] = useState(false);
	const [ineligibleReasons, setIneligibleReasons] = useState<string[]>([]);
	const [showRidersOnTeam, setShowRidersOnTeam] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [editTeamView, setEditTeamView] = useState<"default" | "columns">(
		"default",
	);

	// Get query params for sport and series from URL
	const location = useLocation();
	const navigate = useNavigate();
	const searchParams = new URLSearchParams(location.search);
	const sport = searchParams.get("sport");
	const series = searchParams.get("series");
	const leagueId = searchParams.get("leagueId");

	const uniqueTeams = useMemo(() => {
		const teamSet = new Set(
			eligibleRiders.flatMap(
				(rider) =>
					rider.teamHistory
						?.filter(
							(history: TeamHistory) => history.season === selectedSeason._id,
						)
						.map((history) => history.team) ?? [],
			),
		);
		return Array.from(teamSet).sort();
	}, [eligibleRiders, selectedSeason]);

	const eligibleBrandTeams = useMemo(() => {
		return brandTeams
			.filter(
				(brandTeam) => brandTeam._id && uniqueTeams.includes(brandTeam._id),
			)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [brandTeams, uniqueTeams]);

	const maleRiders = riders.filter(
		(rider: any) => rider.rider.gender === Gender.male,
	);
	const femaleRiders = riders.filter(
		(rider) => rider.rider.gender === Gender.female,
	);

	const uniqueCountries = useMemo(() => {
		const countrySet = new Set(
			eligibleRiders.map((rider: Rider) =>
				(rider.country ?? "").toUpperCase().trim(),
			),
		);
		return Array.from(countrySet).sort();
	}, [eligibleRiders]);

	//If team changes, setSelectedUCITeam
	useEffect(() => {
		const _brandTeam = brandTeams.find(
			(brandTeam) => brandTeam._id === team?.selectedTeam,
		);
		if (_brandTeam) {
			setSelectedBrandTeam(_brandTeam);
			// const teamRiders = eligibleRiders.filter(
			// 	(rider) => rider.team === team.selectedTeam,
			// );

			//Instead of using eligibleRiders, use the riders from the team
			const teamRiders = eligibleRiders.filter(
				(rider: Rider) =>
					rider.teamHistory?.find(
						(teamHistory: TeamHistory) =>
							teamHistory.season === selectedSeason._id,
					)?.team === _brandTeam._id,
			);

			setTeamRiders(teamRiders);
		} else {
			setSelectedBrandTeam(initialBrandTeam);
		}
	}, [team, brandTeams, eligibleRiders]);

	const fetchBrandTeams = async () => {
		try {
			setBrandTeamLoading(true);
			const res = await getBrandTeams();
			if (res) {
				setBrandTeams(res.data);
			}
		} catch (error) {
			console.error("Error fetching brand teams:", error);
		} finally {
			setBrandTeamLoading(false);
		}
	};

	useEffect(() => {
		fetchBrandTeams();
	}, []);

	useEffect(() => {
		//Get all teams from sortedAndFilteredRiders
		// const teams = eligibleRiders.map((rider) => rider.team);
		// const uniqueTeams = [...new Set(teams)];
		// uniqueTeams.sort();
		// setUciTeams(uniqueTeams);

		if (team && Array.isArray(team.riders) && team.riders.length > 0) {
			setRiders(team.riders);
		}
	}, [team, eligibleRiders]);

	//on team change, calculate budget remaining
	useEffect(() => {
		const totalValue = riders.reduce(
			(acc, rider) =>
				acc +
				calculateRiderValue(
					rider.rider.ratings?.find((r: Rating) => r.sport === sport)?.rating ??
						0,
				),
			0,
		);
		setBudgetRemaining(budget - totalValue);
	}, [riders]);

	const addRiderToTeam = (rider: Rider) => {
		if (
			!riders.some((teamRider) => teamRider.rider._id === rider._id) &&
			riders.length < numberOfMen + numberOfWomen
		) {
			setRiders([...riders, { rider: rider, rank: 0 }]);
		}
	};

	const removeRiderFromTeam = (riderId: string) => {
		setRiders(riders.filter((rider) => rider.rider._id !== riderId));
	};

	const getIneligibleReasons = () => {
		const reasons: string[] = [];
		const men = riders.filter((rider) => rider.rider.gender === "m").length;
		const women = riders.filter((rider) => rider.rider.gender === "f").length;

		if (men < numberOfMen) {
			const remaining = numberOfMen - men;
			reasons.push(
				`Add ${remaining} more male rider${remaining === 1 ? "" : "s"}.`,
			);
		} else if (men > numberOfMen) {
			const extra = men - numberOfMen;
			reasons.push(`Remove ${extra} extra male rider${extra === 1 ? "" : "s"}.`);
		}

		if (women < numberOfWomen) {
			const remaining = numberOfWomen - women;
			reasons.push(
				`Add ${remaining} more female rider${remaining === 1 ? "" : "s"}.`,
			);
		} else if (women > numberOfWomen) {
			const extra = women - numberOfWomen;
			reasons.push(
				`Remove ${extra} extra female rider${extra === 1 ? "" : "s"}.`,
			);
		}

		if (!selectedBrandTeam._id) {
			reasons.push("Select a brand team.");
		}

		return reasons;
	};

	const isTeamEligible = getIneligibleReasons().length === 0;

	//set uci team and set riders to all riders that are part of team
	const handleSelectBrandTeam = (_team: BrandTeam) => {
		setSelectedBrandTeam(_team);
		const teamRiders = eligibleRiders.filter(
			(rider: Rider) =>
				rider.teamHistory?.find(
					(teamHistory: TeamHistory) =>
						teamHistory.season === selectedSeason._id,
				)?.team === _team._id,
		);
		setTeamRiders(teamRiders);
	};

	const saveTeam = async () => {
		if (!isLoggedIn) {
			dispatch(setShowLoginModal(true));
			return;
		}

		const reasons = getIneligibleReasons();
		if (reasons.length > 0) {
			setIneligibleReasons(reasons);
			setShowIneligibleModal(true);
			return;
		}

		try {
			if (team?._id && team._id !== "") {
				const teamData: CreateTeamInput = {
					...team,
					riders: riders,
					selectedTeam: selectedBrandTeam?._id ?? "",
					name: team.name ?? "Default Team Name", // Ensure 'name' is always a string
				};

				if (!teamData.selectedTeam) {
					throw new Error("Selected team is undefined.");
				}
				const res = await updateTeam(team._id, teamData);
				if (res) {
					if (leagueId) {
						navigate(`/league/${leagueId}`);
					} else {
						navigate("/fantasy");
					}
				}
			} else {
				if (!selectedBrandTeam._id) {
					setIneligibleReasons(["Select a brand team."]);
					setShowIneligibleModal(true);
					return;
				}

				if (!sport || !series) {
					alert("No Sport or Series found for team");
				}
				const teamData: CreateTeamInput = {
					league: leagueId ? leagueId : null,
					riders: riders,
					selectedTeam: selectedBrandTeam._id,
					name: "",
					sport: sport ?? "",
					series: series ?? "",
				};

				const res = await createTeam(teamData);
				if (res) {
					if (leagueId) {
						navigate(`/league/${leagueId}`);
					} else {
						navigate("/fantasy");
					}
				}
			}
		} catch (error) {
			alert("Error saving team:" + error);
		}
	};

	const handleAttemptSave = (ignoreDisable = false) => {
		if (disable && !ignoreDisable) {
			setShowDisabledModal(true);
			return;
		}

		const reasons = getIneligibleReasons();
		if (reasons.length > 0) {
			setIneligibleReasons(reasons);
			setShowIneligibleModal(true);
			return;
		}

		saveTeam();
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(e.target.value);
	};

	const isAddDisabled = (gender: Gender, rider: Rider) => {
		const isAlreadyInTeam = riders.some(
			(teamRider) => teamRider.rider._id === rider._id,
		);
		if (isAlreadyInTeam) return true;

		//check if adding rider will exceed budget
		const totalValue = riders.reduce(
			(acc, rider) =>
				acc +
				calculateRiderValue(
					rider.rider.ratings?.find((r: Rating) => r.sport === sport)?.rating ??
						0,
				), // Calculate total value of riders
			0,
		);

		//check that rider has a rating
		if (!rider.ratings?.find((r) => r.sport === sport)) return true;

		const riderValue = calculateRiderValue(
			rider?.ratings.find((r) => r.sport === sport)?.rating ?? 0,
		);

		if (totalValue + riderValue > budget) return true;

		return gender === "m"
			? riders.filter((r) => r.rider.gender === "m").length >= numberOfMen
			: riders.filter((r) => r.rider.gender === "f").length >= numberOfWomen;
	};

	const aggregateData = (gender: Gender) => {
		const filteredData = uciPoints.filter((item) => item.gender === gender);
		const positionData: any = {};

		filteredData.forEach((item) => {
			if (!positionData[item.position]) {
				positionData[item.position] = { q: "-", s: "-", f: "-" }; // initialize with default values
			}
			if (item.round === "q") {
				positionData[item.position].q = item.points;
			} else if (item.round === "s") {
				positionData[item.position].s = item.points;
			} else if (item.round === "f") {
				positionData[item.position].f = item.points;
			}
		});

		return positionData;
	};

	const menData = aggregateData(Gender.male);
	const womenData = aggregateData(Gender.female);

	// Sort and filter riders before rendering
	const sortedAndFilteredRiders = eligibleRiders
		.filter(
			(rider) =>
				(filterGender ? rider.gender === filterGender : true) &&
				(filterCountry
					? (rider.country?.toLowerCase() ?? "") === filterCountry.toLowerCase()
					: true) &&
				(searchText
					? (rider.first_name?.toLowerCase() ?? "").includes(
							searchText.toLowerCase(),
					  ) ||
					  (rider.last_name?.toLowerCase() ?? "").includes(
							searchText.toLowerCase(),
					  )
					: true),
		)
		.sort((a, b) => {
			let compareA =
				sortKey === "rating"
					? (a.ratings ?? []).find((r: any) => r.sport.toString() === sport)
							?.rating || 0
					: a[sortKey as keyof Rider];
			let compareB =
				sortKey === "rating"
					? b.ratings?.find((r: any) => r.sport.toString() === sport)?.rating ??
					  (0 || 0)
					: b[sortKey as keyof Rider];

			if (sortOrder === "asc") {
				return (compareA ?? 0) > (compareB ?? 0) ? 1 : -1;
			} else {
				return (compareA ?? 0) < (compareB ?? 0) ? 1 : -1;
			}
		});

	const myTeamCard = (
		<Card className="mb-3 bg-secondary text-light border-0">
			<Card.Header className="bg-secondary text-white fs-2 fw-bold">
				My Team
			</Card.Header>
			<Card.Body>
				<BudgetRemainingCard budgetRemaining={budgetRemaining} />
				<div className="ms-4 mb-1 fw-bold">Men</div>
				{maleRiders.map((rider: EditTeamRider) => (
					<BudgetModeRiderCard
						rider={rider}
						sport={sport ?? ""}
						removeRiderFromTeam={removeRiderFromTeam}
					/>
				))}
				<div className="ms-4 mt-3 mb-1 fw-bold">Women</div>
				{femaleRiders.map((rider: EditTeamRider) => (
					<BudgetModeRiderCard
						rider={rider}
						sport={sport ?? ""}
						removeRiderFromTeam={removeRiderFromTeam}
					/>
				))}
				<SelectBrandTeamCard
					selectedBrandTeam={selectedBrandTeam}
					brandTeams={eligibleBrandTeams}
					brandTeamLoading={brandTeamLoading}
					handleSelectBrandTeam={handleSelectBrandTeam}
					setShowRidersOnTeam={setShowRidersOnTeam}
				/>
			</Card.Body>
			<Card.Footer>
				<Row>
					<Col className="w-100 text-end">
						<Button
							onClick={() => handleAttemptSave()}
							variant={isTeamEligible ? "primary" : "secondary"}
							className="px-4 "
						>
							<FaSave className="me-2 mb-1" />
							Save Team
						</Button>
					</Col>
				</Row>
			</Card.Footer>
		</Card>
	);

	const eligibleRidersCard = (
		<Card className="mb-3 bg-secondary text-light border-0">
			<Card.Header className="fs-3 fw-bold">Eligible Riders</Card.Header>
			<Card.Body>
				<Row className="mb-2">
					<Col md={2} className="mb-1">
						<Form.Select
							size="sm"
							className="w-100 bg-dark text-success border-0 fw-bold fs-6 "
							onChange={(e) => {
								const [key, order] = e.target.value.split(",");
								setSortKey(key);
								setSortOrder(order);
							}}
							value={`${sortKey},${sortOrder}`}
						>
							<option value="last_name,asc">Last Name Asc</option>
							<option value="last_name,desc">Last Name Desc</option>
							<option value="rating,asc">Value Asc</option>
							<option value="rating,desc">Value Desc</option>
						</Form.Select>
					</Col>
					<Col md={2} className="text-center justify-content-center mb-1">
						<Form.Select
							size="sm"
							className="w-100 bg-dark text-success border-0 fw-bold fs-6"
							onChange={(e) => setFilterGender(e.target.value)}
							value={filterGender}
						>
							<option value="">Gender</option>
							<option value="m">Male</option>
							<option value="f">Female</option>
						</Form.Select>
					</Col>
					<Col md={2}>
						<Form.Select
							size="sm"
							value={filterCountry}
							onChange={(e) => setFilterCountry(e.target.value)}
							className="w-100 bg-dark text-success border-0 fw-bold fs-6"
						>
							<option value="">Country</option>
							{uniqueCountries.map((country) => (
								<option key={country} value={country}>
									{country}
								</option>
							))}
						</Form.Select>
					</Col>
				</Row>
				<Row className="mb-3">
					<Col>
						<Form.Group>
							<Form.Control
								type="search"
								placeholder="Search for a rider..."
								className="bg-dark text-white border-0 custom-placeholder rounded-pill"
								style={{ maxWidth: "20rem" }}
								onChange={handleSearch}
							/>
						</Form.Group>
					</Col>
				</Row>
				{sortedAndFilteredRiders.map((rider: Rider) => (
					<BudgetModeEligibleRiderCard
						rider={rider}
						sport={sport ?? ""}
						isAddDisabled={isAddDisabled}
						addRiderToTeam={addRiderToTeam}
					/>
				))}
			</Card.Body>
		</Card>
	);

	return (
		<div>
			<SeasonRulesCard />
			<BudgetModeRulesCard
				numberOfMen={numberOfMen}
				numberOfWomen={numberOfWomen}
				budget={budget}
				setViewScoring={setViewScoring}
			/>
			<Row className="mb-3 d-none d-md-flex">
				<Col className="d-flex justify-content-md-end justify-content-center">
					<div className="btn-group" role="group" aria-label="Edit team view toggle">
						<Button
							variant={editTeamView === "default" ? "primary" : "outline-light"}
							onClick={() => setEditTeamView("default")}
						>
							Standard View
						</Button>
						<Button
							variant={editTeamView === "columns" ? "primary" : "outline-light"}
							onClick={() => setEditTeamView("columns")}
						>
							Column View
						</Button>
					</div>
				</Col>
			</Row>

			{editTeamView === "columns" ? (
				<Row className="g-3">
					<Col xl={6}>{eligibleRidersCard}</Col>
					<Col xl={6}>{myTeamCard}</Col>
				</Row>
			) : (
				<>
					{myTeamCard}
					{eligibleRidersCard}
				</>
			)}

			<UCIPointsModal
				viewScoring={viewScoring}
				setViewScoring={setViewScoring}
				uciTeamPoints={uciTeamPoints}
			/>
			{/* <Modal
				show={viewScoring}
				onHide={() => setViewScoring(false)}
				size="lg"
				centered
			>
				<Modal.Header
					closeButton
					
				>
					<Modal.Title>
						<b>UCI Downhill</b> <LuDot className="mb-1 me-2" />
						Rider Points
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="px-md-5">
					<Tabs
						defaultActiveKey="men"
						id="uncontrolled-tab-example"
						className="mb-3"
					>
						<Tab eventKey="men" title="Men" style={{ overflow: "auto" }}>
							<Table striped bordered hover style={{ overflowX: "auto" }}>
								<thead>
									<tr>
										<th>
											<MdFormatListNumbered />
										</th>
										<th>Finals</th>
										<th>Semis</th>
										<th>Qualifying</th>
									</tr>
								</thead>
								<tbody>
									{Object.entries(menData).map(([position, points]) => {
										const typedPoints = points as {
											q: string | number;
											s: string | number;
											f: string | number;
										};
										return (
											<tr key={position}>
												<td>{position}.</td>
												<td>{typedPoints.f}</td>
												<td>{typedPoints.s}</td>
												<td>{typedPoints.q}</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</Tab>
						<Tab eventKey="women" title="Women" style={{ overflow: "auto" }}>
							<Table striped bordered hover>
								<thead>
									<tr>
										<th>
											<MdFormatListNumbered />
										</th>
										<th>Finals</th>
										<th>Semis</th>
										<th>Qualifying</th>
									</tr>
								</thead>
								<tbody>
									{Object.entries(womenData).map(([position, points]) => {
										const typedPoints = points as {
											q: string | number;
											s: string | number;
											f: string | number;
										};
										return (
											<tr key={position}>
												<td>{position}.</td>
												<td>{typedPoints.f}</td>
												<td>{typedPoints.s}</td>
												<td>{typedPoints.q}</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</Tab>
						<Tab eventKey={"team"} title="Team" style={{ overflow: "auto" }}>
							<div className="mb-3 fst-italic" style={{ fontSize: "0.8rem" }}>
								{" "}
								** Please note that team points have be created and calculated
								by The Race Companion and in no way reflect official UCI Team
								Points.{" "}
							</div>
							<Table striped bordered hover>
								<thead>
									<tr>
										<th>
											<MdFormatListNumbered />
										</th>
										<th>Points</th>
									</tr>
								</thead>
								<tbody>
									{uciTeamPoints.map((team) => (
										<tr key={team.Position}>
											<td>{team.Position}.</td>
											<td>{team.Points}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</Tab>
					</Tabs>
				</Modal.Body>
			</Modal> */}
			<Modal
				show={showDisabledModal}
				onHide={() => setShowDisabledModal(false)}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<IoWarning className="me-2 mb-1" />
						Event Underway
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>An event is currently underway.</p>
					<p>
						Changes to your team will no longer be reflected in the current
						event.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="primary"
						onClick={() => {
							handleAttemptSave(true);
						}}
					>
						Save Team
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal
				show={showIneligibleModal}
				onHide={() => setShowIneligibleModal(false)}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						<IoWarning className="me-2 mb-1" />
						Team Ineligible
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Complete the following before saving your team:</p>
					<ul className="mb-0">
						{ineligibleReasons.map((reason) => (
							<li key={reason}>{reason}</li>
						))}
					</ul>
				</Modal.Body>
			</Modal>
			<Modal
				show={showRidersOnTeam}
				onHide={() => setShowRidersOnTeam(false)}
				size="lg"
				centered
			>
				<Modal.Header closeButton >
					<Modal.Title>
						{" "}
						<HiUserGroup className="me-2 mb-1" />
						{selectedBrandTeam.name}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Table striped hover size="sm">
						<tbody>
							{teamRiders.map((rider) => (
								<tr key={rider._id}>
									<td>
										{getCountryISO2(rider.country.toUpperCase().trim()) ? (
											<ReactCountryFlag
												countryCode={getCountryISO2(
													rider.country.toUpperCase().trim(),
												)}
												svg
												style={{ width: "2rem" }}
											/>
										) : (
											rider.country
										)}
										<b className="ms-3">{rider.last_name}</b>,{" "}
										{rider.first_name}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default BudgetMode;
